import gql from 'graphql-tag';
import moment from 'moment';

import path from 'js/lib/path';
import { MED_DATETIME_NO_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { DegreeItemProgressState, Item } from 'bundles/degree-home/types/Course';
import type { ItemType } from 'bundles/item/types/ItemType';
import getUrlTypeName from 'bundles/ondemand/utils/getUrlTypeName';
import toHumanReadableTypeName from 'bundles/ondemand/utils/toHumanReadableTypeName';

import _t from 'i18n!nls/degree-home';

export function getFormattedShortDate(item: Item): string {
  return formatDateTimeDisplay(item.deadline.dueAt, MED_DATETIME_NO_YEAR_DISPLAY);
}
getFormattedShortDate.fragment = gql`
  fragment DueAtFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
    deadline {
      dueAt
    }
  }
`;

export function isOverdue(item: Item): boolean {
  return item.deadline.isOverdue;
}
isOverdue.fragment = gql`
  fragment OverdueFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
    deadline {
      isOverdue
    }
  }
`;

/**
 * Get a prefix for displaying in front of the item's title. This prefix contains information about the type of
 * item and the track that the item is in.
 *
 * TODO (billy) get honors trackId
 */
export function getTitlePrefix(item: Item): string {
  return toHumanReadableTypeName(item.typeName, '', item.contentSummary);
}
getTitlePrefix.fragment = gql`
  fragment TitlePrefixFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
    contentSummary {
      ... on DegreeLearnerProgressesV1_staffGradedMember {
        staffGraded {
          isTeamSubmissionEnabled
        }
      }
      ... on DegreeLearnerProgressesV1_phasedPeerMember {
        phasedPeer {
          isMentorGraded
        }
      }
    }
  }
`;

function urlTypeName(item: Item): string {
  return getUrlTypeName(item.typeName);
}
urlTypeName.fragment = gql`
  fragment UrlTypeNameFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
    typeName
  }
`;

function isReviewPartForSplitPeer(item: Item): boolean {
  return item.typeName === 'splitPeerReviewItem';
}
isReviewPartForSplitPeer.fragment = gql`
  fragment IsReviewPartForSplitPeerFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
    typeName
  }
`;

/**
 * Get the relative entry link to the item, relative to the course, of the form :itemType/:itemId/:itemSlug.
 */
function getCourseRelativeLink(item: Item): string {
  let url = path.join(urlTypeName(item), item.id, item.slug);
  if (isReviewPartForSplitPeer(item)) {
    url = path.join(url, 'give-feedback');
  }
  return url;
}
getCourseRelativeLink.fragment = gql`
  fragment CourseRelativeLinkFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
    id
    slug
  }
`;

/**
 * Get the entry link to the item, of the form /learn/:courseSlug/:itemType/:itemId-:itemSlug/[...]
 */
export function getItemLink(item: Item, courseSlug: string): string {
  return path.join(`/learn/${courseSlug}`, getCourseRelativeLink(item));
}
getItemLink.fragment = gql`
  fragment LinkFragment on DegreeLearnerProgressesV1_org_coursera_degree_learner_ItemState {
    slug
    typeName
  }
`;

export function getIsAssignmentPartForSplitPeer(item: Item): boolean {
  const { typeName } = item;
  return (
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    item.contentSummary[typeName] &&
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    !item.contentSummary[typeName].isMentorGraded &&
    ['phasedPeer', 'gradedPeer', 'closedPeer'].indexOf(typeName) !== -1
  );
}

export function getProgressStateLabel(progressState: DegreeItemProgressState): string {
  const labelTranslationMap = {
    NotStarted: _t('Not Started'),
    Started: _t('Started'),
    InReview: _t('In Review'),
    Completed: _t('Completed'),
    Failed: _t('Failed'),
    Optional: _t('Optional'),
    Overdue: _t('Overdue'),
  };
  return labelTranslationMap[progressState];
}

export function getReviewPartFromSplitPeer(item: Item): Item | undefined {
  if (!getIsAssignmentPartForSplitPeer(item)) {
    return undefined;
  }

  const reviewPartItem = { ...item, typeName: 'splitPeerReviewItem' as ItemType };
  // For graded and closed peer reviews,
  // set a default of 3 days for the review deadline offset.
  const newDeadline = moment(reviewPartItem.deadline.dueAt).add(3, 'day');
  const deadline = { ...reviewPartItem.deadline, dueAt: newDeadline.valueOf() };

  reviewPartItem.deadline = deadline;
  return reviewPartItem;
}
